<template>
        <div>
            <iframe
                class="embed-video"
                type="text/html"
                :src="videoProps"
                frameborder="0"
                mozallowfullscreen
                webkitallowfullscreen
                allowfullscreen
                allow="autoplay; fullscreen"
                ref="vimeoPlayer"
            />
            <CourseTestNotificationForVideo
                    v-if="showCourseTestNotification && videoFinished"
                    v-on:hideCourseTestNotification="hideCourseTestNotification"
                    :isLastCollectionVideo="isLastCollectionVideo"
                    :isCollectionCoursePage="isCollectionCoursePage"
            />
            <VideoRedirectionCountdown v-else-if="videoFinished && displayVideoRedirectionBox"
                :nextVideoTitle="this.videoRedirectionNextSectionTitle"
                :countdown="this.videoRedirectionCountdown"
                v-on:countDownComplete="countDownComplete"
            />
        </div>
</template>

<script>
    import Player from '@vimeo/player'
    import { mapGetters } from 'vuex'
    import config from 'services/config'
    import moment from 'moment'
    import VideoRedirectionCountdown from 'components/coursepage/coursevideo/VideoRedirectionCountdown'
    import videoRedirections from 'components/shared/mixins/videoRedirections'
    import CourseTestNotificationForVideo from './CourseTestNotificationForVideo.vue'
    import microLearningHelper from 'components/shared/mixins/microLearningHelper'
    import courseTestHelper from 'components/shared/mixins/courseTestHelper'
    import AutoPlayHelper from 'components/coursepage/coursevideo/helper/AutoPlayHelper'

    export default {
        name: 'VimeoVideo',
        mixins: [ microLearningHelper, videoRedirections, courseTestHelper, AutoPlayHelper ],
        components: {
            VideoRedirectionCountdown,
            CourseTestNotificationForVideo
        },
        data () {
            return {
                player: null,
                allCurrentlyWatchedSequences: [],
                duration: 0,
                demoTimeout: null,
                initializeTimer: null,
                blockDemoVideo: false,
                currentPosition: null,
                lastHash: null,
                videoTimeout: null,
                demoTimer: null,
                progressInterval: null,
                videoFinished: false,
                showCourseTestNotification: false,
                playerType: 'Vimeo',
                isPlaying: false,
                liveDemoTimeInSeconds: 45,
                videoInterval: null,
                saveVideoInterval: null
            }
        },
        watch: {
            moveTarget: {
                handler: 'moveVideoToPoint'
            },
            isRatingModalVisible: {
                handler: 'pauseVideoIfRatingModalIsVisible'
            },
            errorException () {
                if (this.errorException) {
                    this.player.pause()
                }
            }
        },
        computed: {
            ...mapGetters({
                moveTarget: 'VideoPlayer/moveTarget',
                showOnlyDemo: 'VideoPlayer/showOnlyDemo',
                demoWatchTimeLimit: 'VideoPlayer/demoWatchTimeLimit',
                videoProps: 'VideoPlayer/videoProps',
                videoStartPosition: 'VideoPlayer/position',
                shouldAutoPlay: 'VideoPlayer/shouldAutoPlay',
                liveWatchedTimeById: 'getLiveWatchedTime',
                liveWatchedTime: 'VideoPlayer/getLiveWatchedTime',
                isRatingModalVisible: 'VideoPlayer/getIsRatingModalVisible',
                errorException: 'VideoLog/getErrorException'
            }),
            getVideoLogIntervalTime () {
                const now = moment().unix()
                return now > config.customVideoLogIntervalStartTime &&
                    now < config.customVideoLogIntervalEndTime ? config.customVideoLogIntervalMilliseconds : 30000
            },
            isLive () {
                return this.videoProps.includes('event') && this.duration === 0
            },
            getDemoTimeOutDuration () {
                if (this.isLive) {
                    return (this.liveDemoTimeInSeconds * 1000)
                }
                if (this.$totemLang === 'se') {
                    return 60000
                }
                return Math.round(this.duration * 0.05 * 1000)
            }
        },
        methods: {
            async moveVideoToPoint (point) {
                if (point > await this.player.getDuration()) {
                    point = 0
                }
                if (this.player) {
                    try {
                        this.player.setCurrentTime(point)
                    } catch (e) {
                        console.log(e)
                        // PROBABLY NO NEED TO LOGIN ANYTHING.
                    }
                }
            },
            async onDemoExpired () {
                try {
                    if (!this.isLive) {
                        await this.player.setCurrentTime(0)
                    }
                    await this.player.pause()
                    clearTimeout(this.demoTimeout)
                    this.demoTimeout = null
                    this.emitDemoFinished()
                } catch (e) {
                    window.location.reload(true)
                }
            },
            tryInitializePlayer () {
                if (this.$refs.vimeoPlayer) {
                    clearInterval(this.initializeTimer)
                    this.initializePlayer()
                } else {
                    this.initializeTimer = setTimeout(this.tryInitializePlayer, 50)
                }
            },
            async onLoaded () {
                try {
                    if (!this.showOnlyDemo && +this.videoStartPosition !== 0) {
                        await this.moveVideoToPoint(this.videoStartPosition)
                    }
                    this.duration = await this.player.getDuration()

                    if ((this.course.type.value === 'webinar' && !this.showOnlyDemo)) {
                        this.$store.dispatch('VideoPlayer/updateLiveWatchedTime', this.liveWatchedTimeById(this.course.id))
                    }
                    if (!this.showOnlyDemo) {
                        this.videoInterval = setInterval(() => this.$emit('persistPlayedTime'), 1000)
                        this.saveVideoInterval = setInterval(() => this.$emit('saveVideoLog'), this.getVideoLogIntervalTime)
                        this.progressInterval = setInterval(this.supportProgress, 3000)
                    }
                } catch (e) {
                    console.log({ e })
                }
                if (this.shouldAutoPlayVideo) {
                    setTimeout(() => this.player.play(), 250)
                }
            },
            async onPlay () {
                try {
                    if (!this.duration) {
                        throw 'no duration'
                    }
                } catch (e) {
                    window.location.reload(true)
                }

                if (!this.showOnlyDemo) {
                    this.$emit('restoreFromHiddenCourses')
                }
                this.$emit('videoWasPlayed')
            },
            async onPlaying () {
                this.isPlaying = true
                if (this.showOnlyDemo && !this.demoTimeout) {
                    if(this.$totemLang === 'dk') {
                        this.emitDemoFinished()
                    } else {
                        this.demoTimeout = setTimeout(this.onDemoExpired, this.getDemoTimeOutDuration)
                    }
                }
            },
            pauseVideoIfRatingModalIsVisible (isRatingModalVisible) {
                if (isRatingModalVisible) {
                    this.player.pause()
                }
            },
            async getDuration () {
                this.duration = await this.player.getDuration()
            },
            async saveVideoProgress () {
                this.getDuration()
                this.currentPosition = await this.player.getCurrentTime()
                this.currentlyWatchedSequences = await this.player.getPlayed()
                const progress = {
                    currentPosition: this.currentPosition,
                    currentSessionSequences: this.currentlyWatchedSequences,
                    videoDuration: this.isLive ? (this.course.videoDuration * 60) : this.duration
                }
                await this.$emit('progress', progress)
                await this.$emit('persistPlayedTime')
            },
            async supportProgress () {
                try {
                    this.currentlyWatchedSequences = await this.player.getPlayed()
                    this.currentPosition = await this.player.getCurrentTime()
                    if (this.isLive && this.isPlaying) {
                        await this.$store.dispatch('progressLive', this.course.id)
                        this.$store.dispatch('VideoPlayer/updateLiveWatchedTime', this.liveWatchedTimeById(this.course.id))
                    }

                    const progress = {
                        currentPosition: this.currentPosition,
                        currentSessionSequences: this.currentlyWatchedSequences,
                        videoDuration: this.isLive ? (this.course.videoDuration * 60) : this.duration
                    }
                    await this.$emit('progress', progress)
                } catch (e) {}
            },
            initializePlayer () {
                this.player = new Player(this.$refs.vimeoPlayer)
                this.player.on('loaded', this.onLoaded)
                this.player.on('pause', this.onPause)
                this.player.on('ended', this.onPlayerCompleted)
                this.player.on('playing', this.onPlaying)
                if (!this.isLive) {
                    this.player.on('play', this.onPlay)
                    this.player.on('seeking', this.onSeek)
                }
                this.player.on('playbackratechange', this.onPlayBackRateChange)
                const localStoragePlaybackRate = localStorage.getItem('eduhouseVideoPlaybackRate' + this.course.id)
                if (localStoragePlaybackRate) {
                    this.player.setPlaybackRate(localStoragePlaybackRate)
                }
            },
            async onPause () {
                this.saveVideoProgress()
                this.$emit('saveVideoLog')
                this.isPlaying = false
            },
            async onSeek () {
                if (this.showOnlyDemo) {
                    this.onDemoExpired()
                }
                this.saveVideoProgress()
            },
            emitDemoFinished () {
                this.$emit('demoFinished')
            },
            async onPlayBackRateChange (rate) {
                localStorage.setItem('eduhouseVideoPlaybackRate' + this.course.id, rate.playbackRate)
            }
        },
        mounted () {
            this.tryInitializePlayer()
        },
        beforeDestroy () {
            clearTimeout(this.demoTimeout)
            clearInterval(this.initializeTimer)
            clearInterval(this.videoInterval)
            clearInterval(this.saveVideoInterval)
            clearInterval(this.progressInterval)
        }
    }
</script>

<style scoped>
    .vp-video-wrapper transparent {
        border-radius: 10px;
    }

    .embed-video {
        width: 100%;
        height: 100%;
        position: absolute;
        display: block;
        top: 0;
        left: 0;
        border-radius: 10px;

    }

</style>
